import { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { SwipeableCard } from './SwipeableCard';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Heart, X, RotateCcw, Sparkles } from 'lucide-react';
import { Database } from '@/integrations/supabase/types';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { useAnalytics } from '@/hooks/useAnalytics';

type Listing = Database['public']['Views']['v_listings_with_image']['Row'] & { price: number | null };

interface SwipeStackProps {
  listings: Listing[];
  onNeedMoreListings: () => void;
}

export function SwipeStack({ listings, onNeedMoreListings }: SwipeStackProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [swipedListings, setSwipedListings] = useState<{ id: string; liked: boolean }[]>([]);
  const { user } = useAuth();
  const { trackListingSwipe, trackFavoriteAdded, trackFavoriteRemoved } = useAnalytics();

  const currentListing = listings[currentIndex];
  const nextListing = listings[currentIndex + 1];

  useEffect(() => {
    // Request more listings when we're running low
    if (listings.length - currentIndex <= 2) {
      onNeedMoreListings();
    }
  }, [currentIndex, listings.length, onNeedMoreListings]);

  const addToFavorites = async (listing: Listing) => {
    if (!user) {
      toast.error('Please sign in to save favorites');
      return;
    }

    try {
      const { error } = await supabase
        .from('favorites')
        .insert({
          user_id: user.id,
          listing_id: listing.id
        });

      if (error && !error.message.includes('duplicate')) {
        throw error;
      }

      toast.success('Added to favorites!');
    } catch (error) {
      console.error('Error adding to favorites:', error);
      toast.error('Failed to add to favorites');
    }
  };

  const handleSwipeRight = async (listing: Listing) => {
    trackListingSwipe(listing.id, 'right');
    trackFavoriteAdded(listing.id);
    await addToFavorites(listing);
    setSwipedListings(prev => [...prev, { id: listing.id, liked: true }]);
    setCurrentIndex(prev => prev + 1);
  };

  const handleSwipeLeft = (listing: Listing) => {
    trackListingSwipe(listing.id, 'left');
    setSwipedListings(prev => [...prev, { id: listing.id, liked: false }]);
    setCurrentIndex(prev => prev + 1);
  };

  const handleUndo = () => {
    if (swipedListings.length === 0) return;
    
    const lastSwiped = swipedListings[swipedListings.length - 1];
    setSwipedListings(prev => prev.slice(0, -1));
    setCurrentIndex(prev => Math.max(0, prev - 1));
    
    if (lastSwiped.liked) {
      trackFavoriteRemoved(lastSwiped.id);
      // Remove from favorites if it was liked
      removeFavorite(lastSwiped.id);
    }
  };

  const removeFavorite = async (listingId: string) => {
    if (!user) return;

    try {
      await supabase
        .from('favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('listing_id', listingId);
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  const handleButtonSwipe = (direction: 'left' | 'right') => {
    if (!currentListing) return;

    if (direction === 'right') {
      handleSwipeRight(currentListing);
    } else {
      handleSwipeLeft(currentListing);
    }
  };

  if (!currentListing) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Sparkles className="h-8 w-8 text-primary" />
          </div>
          <h3 className="text-lg font-semibold mb-2">You've seen all available businesses!</h3>
          <p className="text-muted-foreground mb-4">
            Check back later for new listings or explore your favorites.
          </p>
          <Button onClick={() => window.location.href = '/favorites'}>
            View My Favorites
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      {/* Card Stack */}
      <div className="relative h-[600px] mb-6">
        <AnimatePresence>
          {/* Next card (background) */}
          {nextListing && (
            <div className="absolute inset-0 scale-95 opacity-50">
              <Card className="h-full overflow-hidden bg-muted/50">
                <div className="aspect-video bg-muted/50"></div>
                <div className="p-6">
                  <h3 className="font-semibold text-muted-foreground">
                    {nextListing.title}
                  </h3>
                </div>
              </Card>
            </div>
          )}

          {/* Current card */}
          <SwipeableCard
            key={currentListing.id}
            listing={currentListing}
            onSwipeLeft={handleSwipeLeft}
            onSwipeRight={handleSwipeRight}
          />
        </AnimatePresence>
      </div>

      {/* Control Buttons */}
      <div className="flex justify-center items-center gap-4">
        <Button
          variant="outline"
          size="lg"
          className="rounded-full w-14 h-14 p-0 border-red-200 hover:border-red-300 hover:bg-red-50"
          onClick={() => handleButtonSwipe('left')}
        >
          <X className="h-6 w-6 text-red-500" />
        </Button>

        <Button
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={handleUndo}
          disabled={swipedListings.length === 0}
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Undo
        </Button>

        <Button
          variant="outline"
          size="lg"
          className="rounded-full w-14 h-14 p-0 border-green-200 hover:border-green-300 hover:bg-green-50"
          onClick={() => handleButtonSwipe('right')}
        >
          <Heart className="h-6 w-6 text-green-500" />
        </Button>
      </div>

      {/* Progress Indicator */}
      <div className="mt-4 text-center text-sm text-muted-foreground">
        {currentIndex + 1} of {listings.length} businesses
      </div>
    </div>
  );
}